/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import WorkspaceQueryManager from '../../managers/WorkspaceQueryManager';
import QueryUtils from '../../meshes/mesh-query/mesh-query-utils';
import { IWorkspaceEnrichedGeometry } from '../../models/IGeometries';
import { IGeometryQueryResult } from '../../models/IPropertyTableQuery';
import { QUERY_RESULT_TYPES } from '../../models/IQueryDefinitions';
import { IDrawnDataItem } from '../../models/IWorkspaceData';
import { IWorkspace } from '../../models/IWorkspaces';
import { MmgAttributeQueryConfiguration } from '../../queries/attribute-queries/query-configuration';
import MikeButton from '../../shared-components/mike-button';
import { MmgGroup } from '../../shared/groups/group';
import { hideLayer } from '../../shared/layers/layer-utils';
import { PROP_KEYS } from '../../shared/operations/download/download-operations';
import { ELEMENT_CATEGORIES } from '../../shared/panels/mesh-panel-constants';
import { MmgPanelSubsection } from '../../shared/panels/panel-subsection';
import mikeSharedTheme from '../../shared/styles/mikeSharedTheme';
import { EPanelActionType } from '../../store/actions/PanelActionType';
import { EWorkspaceQueryActionType } from '../../store/actions/WorkspaceQueryActionType';
import { IGlobalState } from '../../store/reducers';
import { getSpatialQueryLayer } from '../../store/reducers/PanelSettingsReducer';
import WorkspaceGeometrySelectors from '../../store/selectors/WorkspaceGeometrySelectors';
import { store } from '../../store/store';
import { t } from '../../translations/i18n';
import { MmgConnectedGeometryQueryElementTable } from './geometry-query-elements-table';
import { clearGeometrySegmentHighlight, renderGeometrySegment, zoomToGeometrySegment } from './geometry-queries-utils';

const ApplyButtonStyle = css`
  margin-top: ${mikeSharedTheme.spacing(1)};
`;

const updateGeometryQueryResult = (geometryId: string, data: Partial<IGeometryQueryResult>) => {
  store.dispatch({ type: EWorkspaceQueryActionType.GEOMETRY_QUERY_RESULT_UPDATE, geometryId, data });
};

export const MmgConnectedGeometryQuerySummary = () => {
  const getGeometrySelectorInstance = WorkspaceGeometrySelectors.makeGetGeometry();
  const getDrawnDataSelectorInstance = WorkspaceGeometrySelectors.makeGetGeometryDrawnData();
  const getGeometryQueriesSelectorInstance = WorkspaceGeometrySelectors.makeGetGeometryQueries();

  const { projectId, workspaceId, geometryId } = useParams<any>();
  const geometry: IWorkspaceEnrichedGeometry = useSelector((state: IGlobalState) =>
    getGeometrySelectorInstance(state, { geometryId }),
  );
  const geometryQueries = useSelector((state: IGlobalState) =>
    getGeometryQueriesSelectorInstance(state, {
      geometryId,
    }),
  );

  const geometryDrawnData: IDrawnDataItem = useSelector((state: IGlobalState) =>
    getDrawnDataSelectorInstance(state, { geometryId }),
  );
  const workspacePanelSettings = useSelector(
    (state: IGlobalState) => state.PanelSettingsReducer.workspacePanelSettings,
  );

  const queryResponse: any = useSelector((state: IGlobalState) => state.WorkspaceQueryReducer.queryResponse);
  const cachedQueryResult: IGeometryQueryResult | undefined = useSelector(
    (state: IGlobalState) => state.WorkspaceQueryReducer.geometryQueryResults[geometryId],
  );

  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(null);

  const drawnData = geometryDrawnData ? [geometryDrawnData] : [];

  useEffect(
    () => {
      return () => {
        const lastQueryId = getSpatialQueryLayer(workspaceId, geometryId);
        lastQueryId && hideLayer(ELEMENT_CATEGORIES.GEOMETRY, lastQueryId, false);
        clearGeometrySegmentHighlight(geometryId);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const geometryQuerySettings = useMemo(
    () => {
      const layerSettings =
        geometry &&
        workspacePanelSettings &&
        workspacePanelSettings.find((setting) => {
          return setting.id === geometry.id;
        });

      const qd = layerSettings && layerSettings.queryDefinition;
      if (qd && qd.updated && qd.updated === geometry.updated) {
        return qd;
      }
      return null;
    },
    [workspacePanelSettings, geometry],
  );

  const queryResult = useMemo(
    () =>
      geometryQuerySettings && cachedQueryResult && isEqual(cachedQueryResult.queryDefinition, geometryQuerySettings)
        ? cachedQueryResult
        : null,
    [cachedQueryResult, geometryQuerySettings],
  );
  const page = queryResult ? queryResult.page : 0;
  const order = queryResult ? queryResult.order : 'Ascending';

  const [selectionConfirmed, setSelectionConfirmed] = useState(
    () => Boolean(geometryQuerySettings) && QueryUtils.isQueryDefinitionComplete(geometryQuerySettings),
  );

  const pendingOperationId = cachedQueryResult ? cachedQueryResult.operationId : null;

  useEffect(
    () => {
      if (
        pendingOperationId &&
        queryResponse &&
        queryResponse.operationId === pendingOperationId &&
        queryResponse.queryDefinition &&
        queryResponse.queryDefinition.targetItems &&
        queryResponse.queryDefinition.targetItems.includes(geometryId)
      ) {
        updateGeometryQueryResult(geometryId, {
          steps: queryResponse.vtuProperties ? queryResponse.vtuProperties : [],
          propertyNames: queryResponse.propertyNames ? queryResponse.propertyNames : [],
          stepsNumber: queryResponse.totalAmountOfQueryResults ? queryResponse.totalAmountOfQueryResults : 0,
          isFetching: false,
          operationId: null,
        });
      }
    },
    [queryResponse, pendingOperationId, geometryId],
  );

  const fetchSteps = useCallback(
    (querySettings: any, p: number, ord?: string) => {
      setHighlightedRowIndex(null);
      updateGeometryQueryResult(geometryId, {
        queryDefinition: querySettings,
        page: p,
        order: ord ?? 'Ascending',
        isFetching: true,
        operationId: null,
      });

      const resultTablePropertyNames = [PROP_KEYS.MMG_LENGTH];

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { updated, ...queryDefinition } = querySettings;

      WorkspaceQueryManager.createPropertyTableQuery({
        meshId: geometryId,
        workspaceId,
        queryDefinition,
        resultTablePropertyNames,
        resultType: QUERY_RESULT_TYPES.GEOMETRY,
        ordering: ord,
        limit: 10,
        offset: p * 10,
        elementCategory: ELEMENT_CATEGORIES.GEOMETRY,
      }).then((operation: any) => {
        updateGeometryQueryResult(geometryId, { operationId: operation?.id ?? null });
      });
    },
    [geometryId, workspaceId],
  );

  const callOnQueryDefinitionChanged = useCallback(
    (newQueryDefinition) => {
      if (workspaceId) {
        const newQueryId = newQueryDefinition && newQueryDefinition.queryId ? newQueryDefinition.queryId : '';
        const lastQueryId = getSpatialQueryLayer(workspaceId, geometryId);
        lastQueryId && lastQueryId !== newQueryId && hideLayer(ELEMENT_CATEGORIES.GEOMETRY, lastQueryId, false);
      }
      
      clearGeometrySegmentHighlight(geometryId);
      setHighlightedRowIndex(null);

      const queryDefinition = { ...newQueryDefinition, name: PROP_KEYS.MMG_LENGTH };
      const isQueryComplete = QueryUtils.isQueryDefinitionComplete(queryDefinition);
      setSelectionConfirmed(isQueryComplete);
      
      if (isQueryComplete) {
        store.dispatch({
          type: EPanelActionType.SET_MESH_QUERY_PANEL,
          queryDefinition: { ...queryDefinition, updated: geometry.updated },
          workspaceId,
          layerId: geometryId,
        });
      }
    },
    [workspaceId, geometryId, geometry],
  );

  const handleChangeOrder = useCallback(
    (ord: string) => {
      fetchSteps(geometryQuerySettings, page, ord);
    },
    [fetchSteps, page, geometryQuerySettings],
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      fetchSteps(geometryQuerySettings, newPage, order);
    },
    [fetchSteps, order, geometryQuerySettings],
  );

  const handleZoomToElement = useCallback(
    (vtp: string, length: number, rowIndex: number) => {
      const workspace: IWorkspace = store.getState().WorkspaceReducer.workspace;
      renderGeometrySegment(geometryId, vtp, length, workspace);
      zoomToGeometrySegment(vtp, length, workspace);
      setHighlightedRowIndex(rowIndex);
    },
    [geometryId],
  );

  const onApplyToTableClicked = useCallback(
    () => {
      geometryQuerySettings && fetchSteps(geometryQuerySettings, 0);
    },
    [fetchSteps, geometryQuerySettings],
  );

  return (
    <div>
      <MmgGroup groupName={'Query'}>
        <MmgPanelSubsection>
          <MmgAttributeQueryConfiguration
            key={geometryId}
            projectId={projectId}
            workspaceId={workspaceId}
            itemId={geometryId}
            item={geometry}
            itemDrawnDatas={drawnData}
            savedQueries={geometryQueries}
            initialQueryDefinition={geometryQuerySettings ? geometryQuerySettings : undefined}
            onQueryDefinitionChanged={callOnQueryDefinitionChanged}
            selectCriteriaLabel={t('SELECT_QUERY_CRITERIA_LABEL')}
            allowSelectAll={true}
            areGeometrySpatialCriteriaAllowed={false}
            allowPreview={false}
          />
          <MikeButton
            css={ApplyButtonStyle}
            variant="contained"
            disabled={!selectionConfirmed}
            onClick={onApplyToTableClicked}
          >
            {t('QUERY_APPLY_TO_TABLE')}
          </MikeButton>
          <MmgConnectedGeometryQueryElementTable
            steps={queryResult && queryResult.steps ? queryResult.steps : null}
            geometryPropNames={queryResult && queryResult.propertyNames ? queryResult.propertyNames : []}
            zoomToElement={handleZoomToElement}
            setOrder={handleChangeOrder}
            ordering={order}
            isFetching={queryResult ? queryResult.isFetching : false}
            onChangePage={handleChangePage}
            stepsNumber={queryResult && queryResult.stepsNumber ? queryResult.stepsNumber : 0}
            queryPage={page}
            highlightedRowIndex={highlightedRowIndex}
          />
        </MmgPanelSubsection>
      </MmgGroup>
    </div>
  );
};
